import React, { useState, useEffect, useRef } from "react";
import {
	Home,
	Ticket,
	ChevronLeft,
	FolderKanban,
	LogOut,
	ListTodo,
	ArrowLeft,
	X,
} from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import VRSOrbitLogo from "../../../../../Assets/VRSorbit logo 1.png";
import VRSOrbitText from "../../../../../Assets/VRSorbit logo 2.png";
import NotificationAlert from "../../../../Alerts/NotificationAlert";
import ConfirmationAlert from "../../../../Alerts/ConfirmationAlert";
import Cookies from "js-cookie";
import { Logout } from "../../../../../Services/LoginService";

const AdminSidebar = ({ isMobileOpen = false, onCloseMobile }) => {
	const navigate = useNavigate();
	const [activeMenu, setActiveMenu] = useState(null);
	const panelRef = useRef(null);
	const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
	const [alert, setAlert] = useState(null);

	const menuItems = [
		{
			name: "Home",
			icon: Home,
			subItems: [
				{ name: "Dashboard", path: "/Admin-Home" },
				{ name: "Report", path: "/Admin-Report" },
				{ name: "User History", path: "/User-History" },
				{ name: "Manage Users", path: "/Admin/User/Manage-Users" },
			],
		},
		{
			name: "Tickets",
			icon: Ticket,
			subItems: [
				{ name: "All Tickets", path: "/Admin/All-Tickets" },
				{ name: "New Tickets", path: "/Admin/New-Tickets" },
				{ name: "Unassigned Tickets", path: "/Admin/Unassigned-Tickets" },
				{ name: "Rejected Tickets", path: "/Admin/Rejected-Tickets" },
				{ name: "Assigned Tickets", path: "/Admin/Assigned-Tickets" },
				{ name: "In Progress Tickets", path: "/Admin/In-Progess-Tickets" },
				{
					name: "Ready for Review Tickets",
					path: "/Admin/Ready-For-Review-Tickets",
				},
				{
					name: "Assign To Reviewer Tickets",
					path: "/Admin/Assign-To-Reviewer-Tickets",
				},
				{
					name: "Review In Progress Tickets",
					path: "/Admin/Review-In-Progress-Tickets",
				},
				{ name: "Reviewed Tickets", path: "/Admin/Reviewed-Tickets" },
				{ name: "Hold Tickets", path: "/Admin/Hold-Tickets" },
				{ name: "Closed Tickets", path: "/Admin/Closed-Tickets" },
				{ name: "Reopen Tickets", path: "/Admin/Reopen-Tickets" },
			],
		},
		{
			name: "Task",
			icon: ListTodo,
			subItems: [
				{ name: "All Tasks", path: "/Admin/Task/All-Tasks" },
				{ name: "Assigned Task", path: "/Admin/Task/Assigned-Task" },
				{ name: "In Progress Task", path: "/Admin/Task/In-Progress-Task" },
				{ name: "Completed Task", path: "/Admin/Task/Completed-Task" },
				{ name: "Reopen Task", path: "/Admin/Task/Reopen-Task" },
				{ name: "Hold Task", path: "/Admin/Task/Hold-Task" },
				// { name: "Reviewed Tickets", path: "/Reviewed-Tickets" },
				{ name: "Closed Task", path: "/Admin/Task/Closed-Task" },
			],
		},
		{
			name: "Projects",
			icon: FolderKanban,
			subItems: [
				{ name: "Projects", path: "/Admin-Project" },
				// { name: "Add-Developer", path: "/Admin-Project-Team" },
			],
		},
		{
			name: "Back to user",
			icon: ArrowLeft,
			path: "/home",
		},
	];

	// Close side panel when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (panelRef.current && !panelRef.current.contains(event.target)) {
				setActiveMenu(null);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const closeMobileSidebar = () => {
		onCloseMobile?.();
	};

	const handleMenuClick = (item) => {
		if (!item.subItems && item.path) {
			navigate(item.path);
			setActiveMenu(null);
			closeMobileSidebar();
			return;
		}
		setActiveMenu((prev) => (prev === item.name ? null : item.name));
	};

	const handleLogoutClick = () => {
		setShowLogoutConfirm(true);
	};

	const confirmLogout = async () => {
		try {
			const res = await Logout();

			setAlert({
				type: "success",
				message: res.data?.message || "Logged out successfully!",
			});

			Cookies.remove("Orbit_Token");

			setTimeout(() => {
				window.location.href = "/";
			}, 4000);
		} catch (err) {
			setAlert({
				type: "error",
				message: err.response?.data?.message || "Logout failed. Please try again.",
			});
		}
		setShowLogoutConfirm(false);
	};

	return (
		<>
			{isMobileOpen && (
				<button
					type="button"
					className="fixed inset-0 z-40 bg-slate-950/45 backdrop-blur-sm lg:hidden"
					onClick={closeMobileSidebar}
					aria-label="Close navigation menu"
				/>
			)}

			<div
				className={`fixed inset-y-0 left-0 z-50 flex transition-transform duration-300 ease-out lg:translate-x-0 ${
					isMobileOpen ? "translate-x-0" : "-translate-x-full"
				}`}
			>
				<div className="flex h-full max-w-[100vw]">
					<div className="w-20 bg-white shadow-md flex flex-col items-center py-4 border-r theme-sidebar">
						<div className="mb-6 flex h-10 w-full items-center justify-center">
							<img src={VRSOrbitLogo} alt="Logo" className="h-10 w-auto object-contain" />
						</div>
						<button
							type="button"
							onClick={closeMobileSidebar}
							className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition hover:bg-gray-100 lg:hidden"
							aria-label="Close navigation menu"
						>
							<X size={18} />
						</button>
						<nav className="flex-1 flex flex-col items-center space-y-2 overflow-y-auto px-2">
							{menuItems.map(({ name, icon: Icon, subItems, path }) => (
								<button
									key={name}
									onClick={() => handleMenuClick({ name, subItems, path })}
									className={`theme-nav-button flex flex-col items-center justify-center w-14 h-14 rounded-lg hover:bg-blue-50 transition ${
										activeMenu === name
											? "bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white"
											: "text-gray-700"
									}`}
								>
									<Icon size={22} />
									<span className="text-[10px] mt-1 text-center leading-tight">
										{name}
									</span>
								</button>
							))}
						</nav>

						<div className="mt-auto pb-2">
							<button
								onClick={handleLogoutClick}
								className="theme-nav-button flex flex-col items-center justify-center w-14 h-14 rounded-lg text-gray-700 hover:bg-blue-50 hover:text-[#0061A4] transition group"
							>
								<LogOut
									size={22}
									className="group-hover:text-[#0061A4] transition duration-200"
								/>
								<span className="text-[10px] mt-1 group-hover:text-[#0061A4]">
									Logout
								</span>
							</button>
						</div>
					</div>

					{activeMenu && (
						<div
							ref={panelRef}
							className="w-[min(16rem,calc(100vw-5rem))] bg-white border-r shadow-md p-4 overflow-y-auto theme-side-panel"
						>
							<img
								src={VRSOrbitText}
								alt="Logo"
								className="w-[150px] h-5 mt-4 mb-6"
							/>
							<div className="flex justify-between items-center mb-3">
								<h3 className="font-medium text-gray-700">{activeMenu}</h3>
								<ChevronLeft
									size={18}
									onClick={() => setActiveMenu(null)}
									className="cursor-pointer text-gray-500 hover:text-gray-700"
								/>
							</div>

							{menuItems
								.find((item) => item.name === activeMenu)
								?.subItems?.map((sub) => (
									<NavLink
										key={sub.name}
										to={sub.path}
										onClick={closeMobileSidebar}
										className={({ isActive }) =>
											`block px-3 py-2 rounded-md text-sm mb-1 transition-all duration-200 hover:bg-gradient-to-r from-[#0061A4] to-[#00ACED] 
                 hover:text-white ${
												isActive ? "text-gray-600 font-medium" : "text-gray-600"
											}`
										}
									>
										{sub.name}
									</NavLink>
								))}
						</div>
					)}
				</div>
			</div>

			<ConfirmationAlert
				show={showLogoutConfirm}
				title="Logout Confirmation"
				message="Are you sure you want to logout?"
				onConfirm={confirmLogout}
				onCancel={() => setShowLogoutConfirm(false)}
			/>

			<NotificationAlert
				show={alert !== null}
				type={alert?.type}
				message={alert?.message}
				duration={4000}
				onClose={() => setAlert(null)}
			/>
		</>
	);
};

export default AdminSidebar;
